import React, { useState, useEffect, useCallback } from 'react';
import { Search, Star, ArrowUpRight, Zap, Grid, List, Loader2, Users } from 'lucide-react';
import { fetchTools, fetchCategories, quickSearchTools, getMyStackIds, getToolUrl } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import SaveButton from '../components/SaveButton';
import './Tools.css';

export default function Tools() {
  const [allCategories, setAllCategories] = useState(['All']);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [activecat, setActivecat] = useState('All');
  const [view, setView] = useState('grid');
  const [sort, setSort] = useState('score');

  // Load saved tool IDs for this user
  useEffect(() => {
    if (!user) { setSavedIds(new Set()); return; }
    getMyStackIds().then(setSavedIds).catch(() => {});
  }, [user]);

  // Load category pills once
  useEffect(() => {
    fetchCategories().then(setAllCategories).catch(() => {});
  }, []);

  // Load tools whenever category/sort changes (search handled separately, debounced)
  const loadTools = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = search.trim()
        ? await quickSearchTools(search.trim())
        : await fetchTools({ category: activecat, sort });
      // If searching, still respect category filter client-side
      const filtered = search.trim() && activecat !== 'All'
        ? data.filter(t => t.category === activecat)
        : data;
      setTools(filtered);
    } catch (e) {
      console.error(e);
      setError('Could not load tools right now. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activecat, sort, search]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => { loadTools(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, activecat, sort]);

  return (
    <main className="tools-page" style={{ paddingTop: 'var(--nav-h)' }}>
      {/* Header */}
      <section className="section">
        <div className="section-inner">
          <div className="section-label"><Zap size={12} /> Tool Catalog</div>
          <h1 className="section-title">
            Find your <span className="grad-text">perfect tool</span>
          </h1>
          <p className="section-sub">
            {tools.length > 0 ? `${tools.length} tools` : 'Verified tools'} across {allCategories.length - 1} categories. Updated daily.
          </p>
        </div>
      </section>

      {/* Search + Filters */}
      <div className="tools-page__controls" style={{ padding: '0 var(--sp-6) var(--sp-8)' }}>
        <div className="section-inner">
          <div className="tools-page__search glass-hi">
            <Search size={18} className="tools-page__search-icon" />
            <input
              className="tools-page__search-input"
              placeholder="Search by name, category, or use case..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button onClick={() => setSearch('')} className="tools-page__search-clear">✕</button>
            )}
          </div>

          <div className="tools-page__filters">
            <div className="tools-page__cats">
              {allCategories.map(c => (
                <button
                  key={c}
                  className={`tools-page__cat ${activecat === c ? 'tools-page__cat--active' : ''}`}
                  onClick={() => setActivecat(c)}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="tools-page__right">
              <select
                className="tools-page__sort glass"
                value={sort}
                onChange={e => setSort(e.target.value)}
              >
                <option value="score">Top rated</option>
                <option value="name">A – Z</option>
                <option value="trend">Trending</option>
              </select>
              <div className="tools-page__view-toggle glass">
                <button
                  className={`tools-page__view-btn ${view === 'grid' ? 'active' : ''}`}
                  onClick={() => setView('grid')}
                  aria-label="Grid view"
                ><Grid size={15} /></button>
                <button
                  className={`tools-page__view-btn ${view === 'list' ? 'active' : ''}`}
                  onClick={() => setView('list')}
                  aria-label="List view"
                ><List size={15} /></button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <section style={{ padding: '0 var(--sp-6) var(--sp-20)' }}>
        <div className="section-inner">
          {loading ? (
            <div className="tools-page__empty glass">
              <Loader2 size={28} className="tools-page__spinner" style={{ color: 'var(--text-accent)' }} />
              <p style={{ color: 'var(--text-muted)' }}>Loading tools…</p>
            </div>
          ) : error ? (
            <div className="tools-page__empty glass">
              <p style={{ color: 'var(--error)' }}>{error}</p>
              <button className="btn btn-ghost btn-sm" onClick={loadTools}>Retry</button>
            </div>
          ) : (
            <>
              <p className="tools-page__count">
                Showing <strong>{tools.length}</strong> tools
                {activecat !== 'All' && <> in <strong>{activecat}</strong></>}
              </p>

              {tools.length === 0 ? (
                <div className="tools-page__empty glass">
                  <Search size={32} style={{ color: 'var(--text-muted)' }} />
                  <p style={{ color: 'var(--text-muted)' }}>No tools match "{search}"</p>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setActivecat('All'); }}>
                    Clear filters
                  </button>
                </div>
              ) : view === 'grid' ? (
                <div className="tools-page__grid">
                  {tools.map(tool => <ToolCard key={tool.id} tool={tool} saved={savedIds.has(tool.id)} />)}
                </div>
              ) : (
                <div className="tools-page__list">
                  {tools.map(tool => <ToolRow key={tool.id} tool={tool} saved={savedIds.has(tool.id)} />)}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </main>
  );
}

function ToolCard({ tool, saved = false }) {
  const toolUrl = getToolUrl(tool);
  const isAffiliate = tool.has_affiliate && tool.affiliate_url && !tool.affiliate_url.startsWith('[YOUR_');
  return (
    <div className="tool-page-card glass">
      {/*
        Layout: CSS grid with 2 columns
        Left col (flex: 1): all content — icon, name, desc, meta
        Right col (fixed width): right-edge action column — Trending top, Save bottom
      */}
      <div className="tool-page-card__layout">

        {/* LEFT — all the content */}
        <div className="tool-page-card__content">
          {/* Icon + name */}
          <div className="tool-page-card__top">
            <div className="tool-page-card__icon" style={{ background: `${tool.logo_color}18`, border: `1px solid ${tool.logo_color}30` }}>
              <span style={{ color: tool.logo_color, fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20 }}>
                {tool.name[0]}
              </span>
            </div>
            <div>
              <div className="tool-page-card__name">{tool.name}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <div className="tag">{tool.category}</div>
                {isAffiliate && <span className="tool-page-card__affiliate">Partner</span>}
              </div>
            </div>
          </div>

          {/* Description */}
          <p className="tool-page-card__desc">{tool.tagline}</p>

          {/* Score + users + price */}
          <div className="tool-page-card__meta">
            <div className="tool-card__score">
              <Star size={12} style={{ color:'#fbbf24', fill:'#fbbf24' }} />
              <span>{tool.score}</span>
            </div>
            <div className="tool-card__users">
              <Users size={12} />
              <span>{tool.user_count_display}</span>
            </div>
            <span className="tool-page-card__price">{tool.price_display}</span>
          </div>

          {/* Visit button — bottom-left, shares X row with SaveButton */}
          <a href={toolUrl} target="_blank" rel="noreferrer" className="tool-page-card__cta btn btn-ghost btn-sm">
            Visit {tool.name} <ArrowUpRight size={13} />
          </a>
        </div>

        {/* RIGHT — action column: Trending at top, SaveButton at bottom */}
        <div className="tool-page-card__actions-col">
          <div className="tool-page-card__actions-top">
            {tool.trending && (
              <span className="tool-page-card__trending">
                <Zap size={9} />
                <span>TREND</span>
              </span>
            )}
          </div>
          <div className="tool-page-card__actions-bottom">
            <SaveButton toolId={tool.id} initialSaved={saved} size="sm" />
          </div>
        </div>

      </div>
    </div>
  );
}

function ToolRow({ tool, saved = false }) {
  const toolUrl = getToolUrl(tool);
  const isAffiliate = tool.has_affiliate && tool.affiliate_url && !tool.affiliate_url.startsWith('[YOUR_');
  return (
    <div className="tool-row glass">
      {/* Left: icon */}
      <div className="tool-row__icon" style={{ background: `${tool.logo_color}18`, border: `1px solid ${tool.logo_color}20` }}>
        <span style={{ color: tool.logo_color, fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 16 }}>
          {tool.name[0]}
        </span>
      </div>

      {/* Centre: name + tagline */}
      <div className="tool-row__main">
        <div className="tool-row__name">{tool.name}</div>
        <p className="tool-row__desc">{tool.tagline}</p>
      </div>

      {/* Meta: category + badges + score + price */}
      <div className="tool-row__meta">
        <span className="tag tool-row__cat">{tool.category}</span>
        {tool.trending && <span className="tool-row__badge tool-row__badge--trend"><Zap size={9} /> Trending</span>}
        {isAffiliate && <span className="tool-row__badge tool-row__badge--partner">Partner</span>}
        <div className="tool-row__score">
          <Star size={12} style={{ color:'#fbbf24', fill:'#fbbf24' }} />
          <span>{tool.score}</span>
        </div>
        <span className="tool-row__price">{tool.price_display}</span>
      </div>

      {/* Actions: save + visit */}
      <div className="tool-row__actions">
        <SaveButton toolId={tool.id} initialSaved={saved} size="sm" />
        <a href={toolUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
          Visit <ArrowUpRight size={13} />
        </a>
      </div>
    </div>
  );
}
